import { enforceIdle } from './enforce';
import { getPolicy } from './policy';
import type { IdleDecision, SessionPolicy } from './types';

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * Resolved on first use, not at import time.
 *
 * `getPolicy()` reads the page host, which pulls in the hostname helpers, and this
 * module is imported by `shared/cors-auth-recovery` — deep inside the shared-module
 * graph. Calling it while this module is still initializing therefore risks an import
 * cycle (it is a hard TDZ error on the Knox branch, where the policy is host-derived).
 * Nothing needs the policy until the first decision, so read it then.
 */
let policy: SessionPolicy | null = null;
let lastActivityAt = Date.now();

/**
 * Whether this module instance belongs to a single browser tab.
 *
 * The clock is a module singleton on purpose (see {@link idleClock}), but `'use client'`
 * modules are also evaluated during server rendering — and there the singleton is shared
 * by every request the Node process handles rather than by one user. Writing to it on the
 * server lets one request decide what later ones are served, and the {@link expired}
 * latch makes that permanent: only {@link idleClock.extendConfirmed} clears it and that
 * only ever runs client-side, so one idle-expired document would pin "Session locked"
 * into the SSR output for every subsequent user until the process restarted.
 *
 * So on the server every entry point below is inert — no writes, and no decision derived
 * from process-wide state. Nothing is lost: server-side idle enforcement is owned
 * per-request by proxy.ts and the init routes, which read the exact per-pool activity
 * cookie, and SessionProvider renders the lock for a genuinely expired request from its
 * own props. Covered by lib/session/SessionProvider.ssr.test.tsx.
 */
const isBrowser = typeof window !== 'undefined';

function currentPolicy(): SessionPolicy {
    policy ??= getPolicy();
    return policy;
}

/**
 * True once the clock has actually crossed the idle limit. This is a **latch**:
 * local input cannot clear it. {@link idleClock.extendConfirmed} clears it after
 * the server has agreed the session is still alive, and
 * {@link idleClock.adoptRemoteActivity} clears it when another tab of the same
 * pool reports a newer stamp that is still inside the window.
 *
 * This is the fix for TR-3393 defect 3. The idle clock advances on DOM activity
 * (`SESSION_ACTIVITY_EVENTS`, which includes `mousemove`), but the *evaluation* of
 * that clock only ran on a `setInterval` — and intervals are suspended while the
 * machine sleeps. So the mouse movement that woke the display stamped
 * `lastActivityAt = now` before the first tick could observe the real gap, and the
 * timeout was recomputed as ~0 idle. A user who walked away for 103 minutes came
 * back to a live session.
 *
 * Latching removes the race instead of trying to win it: every activity signal is
 * checked against the *stored* stamp first, so a wake-up event cannot revive a
 * session that already crossed the deadline. Ordering between the wake event and
 * the timer tick stops mattering.
 */
let expired = false;

/**
 * Whether the authoritative server stamp has been observed yet. Until it has, the
 * module-init value of `lastActivityAt` is an optimistic "just now" — a fresh page
 * load must not expire itself before it knows the truth. Once the server speaks,
 * its value wins outright even if it is *older* than the optimistic default, which
 * is exactly the walked-away case.
 */
let seeded = false;

function notifyListeners(): void {
    listeners.forEach((listener) => listener());
}

/**
 * Evaluate the idle decision against the stored stamp, setting the latch if the
 * limit has been crossed. Reports whether the latch changed so callers can avoid
 * notifying subscribers on every one of the many activity events that follow.
 */
function latch(now: number): { decision: IdleDecision; changed: boolean } {
    // Server rendering: never latch, and never answer from process-wide state. Reading
    // alone is not safe here — `lastActivityAt` defaults to module-init time, so after 30
    // minutes of process uptime this would start latching `expired` for every request.
    if (!isBrowser) {
        return { decision: { kind: 'ok' }, changed: false };
    }

    if (expired) {
        return { decision: { kind: 'expired' }, changed: false };
    }

    const decision = enforceIdle({ lastActivityMs: lastActivityAt, policy: currentPolicy(), now });
    if (decision.kind === 'expired') {
        expired = true;
        return { decision, changed: true };
    }
    return { decision, changed: false };
}

/**
 * The authoritative client-side idle clock, shared by the React tree
 * (`SessionProvider`) and by plain modules that cannot read React context — most
 * importantly `shared/cors-auth-recovery`, which gates direct-to-cell-API calls.
 *
 * Module singleton rather than context on purpose: TR-3393 requires that a session
 * idling with only cell-API traffic still times out, and those clients are not
 * React components.
 */
export const idleClock = {
    getPolicy: (): SessionPolicy => currentPolicy(),

    getLastActivityAt: (): number => lastActivityAt,

    /**
     * Idle decision as of `now`, setting the latch as a side effect.
     *
     * Deliberately does **not** notify subscribers: `SessionProvider` reads this
     * during render, and notifying there would push a setState into the render
     * phase. Callers that drive the UI (the evaluation interval and the wake
     * listeners) re-render themselves, and every state-*advancing* entry point
     * below notifies, so nothing is missed.
     */
    decisionAt(now: number = Date.now()): IdleDecision {
        return latch(now).decision;
    },

    isExpired(): boolean {
        return this.decisionAt().kind === 'expired';
    },

    /**
     * Record user activity. Returns false when the activity was rejected because
     * the session had already gone idle — the caller is past the deadline and is
     * being signed out, not kept alive.
     */
    markActivity(activityAt: number = Date.now()): boolean {
        // No DOM activity exists during SSR; accept without recording so a caller that
        // runs in both environments is not told its session expired.
        if (!isBrowser) return true;

        const { decision, changed } = latch(Date.now());
        if (decision.kind === 'expired') {
            if (changed) notifyListeners();
            return false;
        }

        if (activityAt > lastActivityAt) {
            lastActivityAt = activityAt;
            notifyListeners();
        }
        return true;
    },

    /**
     * Adopt activity reported by another tab of this same pool.
     *
     * Unlike {@link markActivity}, the stamp is applied before the deadline
     * check. A background tab's own stamp can be far older than the tab the
     * user is actually using; latching first would expire a session that the
     * other tab has been keeping alive. A stamp that is still past the limit
     * leaves the latch set. Local wake-up input keeps using {@link markActivity},
     * which cannot revive an already-dead session.
     */
    adoptRemoteActivity(activityAt: number): void {
        if (!isBrowser) return;
        if (!Number.isFinite(activityAt) || activityAt <= lastActivityAt) return;

        lastActivityAt = activityAt;
        seeded = true;

        const decision = enforceIdle({
            lastActivityMs: lastActivityAt,
            policy: currentPolicy(),
            now: Date.now(),
        });
        expired = decision.kind === 'expired';
        notifyListeners();
    },

    /**
     * Replace the stamp with another pool's server clock.
     *
     * The root layout seeds whichever pool the route maps to. A page that then
     * authenticates with the other pool (analytics, admin, a viewer) has to
     * drop that seed, including backwards, or the client warning keeps the
     * route pool's idle.
     */
    adoptPoolStamp(activityAt: number): void {
        if (!isBrowser) return;
        if (!Number.isFinite(activityAt)) return;

        lastActivityAt = activityAt;
        seeded = true;
        const decision = enforceIdle({
            lastActivityMs: lastActivityAt,
            policy: currentPolicy(),
            now: Date.now(),
        });
        expired = decision.kind === 'expired';
        notifyListeners();
    },

    /**
     * Adopt the server's `tai-last-activity` stamp (from the root layout's cookie
     * read, or `/api/init`). The first observation replaces the optimistic default
     * outright — including backwards — so a page loaded after a long absence starts
     * from the real stamp rather than from "now".
     */
    observeServerActivity(activityAt: number): void {
        // The hot path for the cross-request leak: SessionProvider seeds from the root
        // layout's cookie read, which is a different user's stamp on every request.
        if (!isBrowser) return;
        if (!Number.isFinite(activityAt)) return;

        const next = seeded ? Math.max(lastActivityAt, activityAt) : activityAt;
        seeded = true;

        const changedStamp = next !== lastActivityAt;
        lastActivityAt = next;

        const { changed } = latch(Date.now());
        if (changed || changedStamp) notifyListeners();
    },

    /**
     * Clear the latch after the server has confirmed the session is still valid
     * (a 2xx from `POST /api/session/extend`). The server enforces the same idle
     * limit, so it refuses this past the deadline — which is what makes the server
     * the only actor able to un-expire a session.
     */
    extendConfirmed(activityAt: number = Date.now()): void {
        if (!isBrowser) return;

        expired = false;
        seeded = true;
        lastActivityAt = activityAt;
        notifyListeners();
    },

    subscribe(listener: Listener): () => void {
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    },

    /** Test seam. Not used in production code. */
    __resetForTests(now: number = Date.now(), nextPolicy: SessionPolicy = getPolicy()): void {
        policy = nextPolicy;
        lastActivityAt = now;
        expired = false;
        seeded = false;
        listeners.clear();
    },
};
